import {
  LucideChevronLeft,
  LucideChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { StudentReportCard } from "@/components/entities/StudentReportCard";

/**
 * Renders a paginated list of student report card rows inside a Report Folder.
 * Loading and empty states are handled by the parent page.
 *
 * Completion state is read from each report's `checkedState` field
 * (`"complete"` | `"incomplete"`). The parent page owns the data and
 * is responsible for persisting toggles via the API.
 *
 * Props:
 *   reports           - array of report objects { _id, studentName, checkedState, data: { oen } }
 *   reportFolderId    - Report Folder ID used to build edit links
 *   size              - "sm" | "md" (default "md") - passed to each card
 *   totalPages        - total pages for pagination
 *   currentPage       - current page index (0-based)
 *   onPageChange      - (newPage) => void
 *   onDelete          - (card) => void
 *   onToggleComplete  - (card) => void - parent persists; checkbox is hidden if missing
 *   canEdit           - boolean (default true) - when false, checkbox is shown but disabled
 *   showCheck         - boolean - passed to each card (card default: true for md, false for sm)
 *   showOen           - boolean - passed to each card (card default: true)
 *   showName          - boolean - passed to each card (card default: true)
 *   showMenu          - boolean - passed to each card (card default: true for md, false for sm)
 *   onClick           - (card) => void - called on card link click
 */
export function StudentReportList({
  reports = [],
  reportFolderId,
  getLinkPath,
  linkState,
  size,
  totalPages = 1,
  currentPage = 0,
  onPageChange,
  onDelete,
  onToggleComplete,
  canEdit = true,
  showCheck,
  showOen,
  showName,
  showMenu,
  onClick,
}) {
  return (
    <div className="space-y-2">
      {reports.map((card) => (
        <StudentReportCard
          key={card._id}
          card={card}
          reportFolderId={reportFolderId}
          linkPath={getLinkPath ? getLinkPath(card) : undefined}
          linkState={linkState}
          size={size}
          isComplete={card.checkedState === "complete"}
          onToggleComplete={onToggleComplete}
          canEdit={canEdit}
          onDelete={onDelete}
          showCheck={showCheck}
          showOen={showOen}
          showName={showName}
          showMenu={showMenu}
          onClick={onClick ? () => onClick(card) : undefined}
        />
      ))}

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange?.(currentPage - 1)}
            disabled={currentPage === 0}
            className="gap-1"
          >
            <LucideChevronLeft size={14} />
            Prev
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {currentPage + 1} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange?.(currentPage + 1)}
            disabled={currentPage === totalPages - 1}
            className="gap-1"
          >
            Next
            <LucideChevronRight size={14} />
          </Button>
        </div>
      )}
    </div>
  );
}
